import { useEffect } from 'react';
import { StepFrame } from './frame';
import type { StepDef } from './steps';
import type { TripWizardConfig, WizardValues } from './types';

interface StepListProps {
  steps: StepDef[];
  values: WizardValues;
  set: <K extends keyof WizardValues>(key: K, value: WizardValues[K]) => void;
  patch: (partial: Partial<WizardValues>) => void;
  config: TripWizardConfig;
  /** Section to scroll to and focus on mount — set when arriving from Review. */
  focusStepId?: string | null;
  /** Steps rendered read-only (e.g. countries/dmc can't change on an update). */
  lockedStepIds?: string[];
}

const sectionId = (stepId: string) => `trip-step-${stepId}`;

/**
 * Every step stacked on one scrollable screen. Used by edit mode and by the
 * review screen's per-section edit, so fixing one answer never means walking
 * the whole questionnaire again.
 */
export function StepList({
  steps,
  values,
  set,
  patch,
  config,
  focusStepId,
  lockedStepIds,
}: StepListProps) {
  const { t } = config;

  useEffect(() => {
    if (!focusStepId) return;
    const section = document.getElementById(sectionId(focusStepId));
    if (!section) return;
    // Runs after the children's effects, so the fields that grab focus on
    // mount (the name input's autoFocus, the country list's inline search)
    // have already had their turn and can't yank the scroll back to the top.
    // Instant rather than smooth: the panel animates in at the same time, and
    // the browser cancels a smooth scroll whose layout moves under it.
    section.scrollIntoView({ block: 'center' });
    section
      .querySelector<HTMLElement>(
        'input:not([type="hidden"]), textarea, button:not([disabled])',
      )
      ?.focus({ preventScroll: true });
  }, [focusStepId]);

  return (
    <div className="mx-auto w-full max-w-2xl">
      {steps.map((step) => (
        <div key={step.id} id={sectionId(step.id)}>
          <StepFrame
            compact
            required={step.required}
            title={t(step.titleKey)}
            description={step.descKey ? t(step.descKey) : undefined}
            requiredLabel={t('required')}
            optionalLabel={t('optional')}
          >
            {lockedStepIds?.includes(step.id) ? (
              // A disabled fieldset natively disables every form control, but
              // not custom ones (the select's <li> option rows) —
              // pointer-events-none makes those inert too.
              <fieldset
                disabled
                className="pointer-events-none m-0 border-none p-0 opacity-60"
              >
                <step.Field
                  values={values}
                  set={set}
                  patch={patch}
                  config={config}
                />
              </fieldset>
            ) : (
              <step.Field
                values={values}
                set={set}
                patch={patch}
                config={config}
              />
            )}
          </StepFrame>
        </div>
      ))}
    </div>
  );
}
